import { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Tabs,
  Tab,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Button,
  Typography,
  Skeleton,
  Collapse,
  IconButton,
  Chip,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { Download, ChevronDown, ChevronRight, CheckCircle2, XCircle } from 'lucide-react';
import { useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { useAuditLogs, exportAuditCsv } from '../../hooks/useAuditLogs';
import { JsonDiff } from '../../components/JsonDiff';
import type { TenantAuditLogDto, EntityAuditLogDto } from '../../types/auditLog';

const EVENT_TYPES = ['LOGIN', 'SETTINGS_CHANGED', 'DATA_EXPORT', 'USER_CREATED', 'USER_DELETED', 'PERMISSION_CHANGED'];
const AUDIT_TYPES = ['Create', 'Update', 'Delete'];
const TABLE_NAMES = ['Institutes', 'Students', 'Employees', 'Fees', 'Classes', 'Users'];

function ExpandableRow({ children, expanded }: { children: React.ReactNode; expanded: boolean }) {
  return (
    <Collapse in={expanded} timeout={180}>
      <Box sx={{ px: 2, pb: 1.5 }}>{children}</Box>
    </Collapse>
  );
}

export default function AuditLogsPage() {
  const [searchParams] = useSearchParams();
  const { useTenantLogs, useEntityLogs } = useAuditLogs();
  const [tab, setTab] = useState(0);

  // Tenant filters
  const [tInstituteId, setTInstituteId] = useState<string>(searchParams.get('instituteId') ?? '');
  const [tEventType, setTEventType] = useState('');
  const [tUserId, setTUserId] = useState('');
  const [tDateFrom, setTDateFrom] = useState('');
  const [tDateTo, setTDateTo] = useState('');
  const [tPage, setTPage] = useState(0);
  const [tPageSize, setTPageSize] = useState(50);
  const [tExpanded, setTExpanded] = useState<number | null>(null);

  // Entity filters
  const [eTableName, setETableName] = useState('');
  const [eAuditType, setEAuditType] = useState('');
  const [eUserId, setEUserId] = useState('');
  const [eDateFrom, setEDateFrom] = useState('');
  const [eDateTo, setEDateTo] = useState('');
  const [ePage, setEPage] = useState(0);
  const [ePageSize, setEPageSize] = useState(50);
  const [eExpanded, setEExpanded] = useState<number | null>(null);

  useEffect(() => {
    const id = searchParams.get('instituteId');
    if (id) { setTInstituteId(id); setTab(0); }
  }, [searchParams]);

  const { data: tenantData, isLoading: loadingTenant } = useTenantLogs({
    pageNumber: tPage + 1,
    pageSize: tPageSize,
    instituteId: tInstituteId ? Number(tInstituteId) : undefined,
    eventType: tEventType || undefined,
    userId: tUserId || undefined,
    dateFrom: tDateFrom || undefined,
    dateTo: tDateTo || undefined,
  });

  const { data: entityData, isLoading: loadingEntity } = useEntityLogs({
    pageNumber: ePage + 1,
    pageSize: ePageSize,
    tableName: eTableName || undefined,
    auditType: eAuditType || undefined,
    userId: eUserId || undefined,
    dateFrom: eDateFrom || undefined,
    dateTo: eDateTo || undefined,
  });

  const handleExport = async () => {
    await exportAuditCsv({
      instituteId: tInstituteId ? Number(tInstituteId) : undefined,
      eventType: tEventType || undefined,
      dateFrom: tDateFrom || undefined,
      dateTo: tDateTo || undefined,
    });
  };

  const tenantColumns: GridColDef<TenantAuditLogDto>[] = [
    {
      field: '_expand',
      headerName: '',
      width: 40,
      sortable: false,
      renderCell: ({ row }) => (
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setTExpanded(tExpanded === row.id ? null : row.id); }}
        >
          {tExpanded === row.id ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </IconButton>
      ),
    },
    {
      field: 'eventTimestamp',
      headerName: 'Time',
      width: 140,
      renderCell: ({ row }) => (
        <Typography variant="body1" sx={{ fontSize: '0.7rem', fontFamily: 'monospace', color: 'text.secondary' }}>
          {dayjs(row.eventTimestamp).format('DD MMM YY HH:mm:ss')}
        </Typography>
      ),
    },
    {
      field: 'instituteName',
      headerName: 'Institute',
      width: 140,
      renderCell: ({ row }) => (
        <Typography variant="body1" sx={{ fontSize: '0.75rem' }} noWrap>
          {row.instituteName ?? `#${row.instituteId}`}
        </Typography>
      ),
    },
    {
      field: 'eventType',
      headerName: 'Event',
      width: 160,
      renderCell: ({ row }) => (
        <Chip
          label={row.eventType}
          size="small"
          sx={{ height: 18, fontSize: '0.6rem', backgroundColor: 'rgba(59,130,246,0.12)', color: '#3B82F6', borderRadius: '4px', '& .MuiChip-label': { px: 1 } }}
        />
      ),
    },
    {
      field: 'userName',
      headerName: 'User',
      flex: 1,
      minWidth: 130,
      renderCell: ({ row }) => (
        <Box>
          <Typography variant="body1" sx={{ fontSize: '0.75rem', fontWeight: 600 }} noWrap>
            {row.userName ?? '—'}
          </Typography>
          <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '0.65rem' }} noWrap>
            {row.userRole ?? ''}
          </Typography>
        </Box>
      ),
    },
    {
      field: 'ipAddress',
      headerName: 'IP',
      width: 110,
      renderCell: ({ row }) => (
        <Typography variant="body1" sx={{ fontSize: '0.7rem', fontFamily: 'monospace', color: 'text.secondary' }}>
          {row.ipAddress ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'isSuccess',
      headerName: '',
      width: 40,
      renderCell: ({ row }) =>
        row.isSuccess ? (
          <CheckCircle2 size={14} color="#10B981" />
        ) : (
          <XCircle size={14} color="#EF4444" />
        ),
    },
  ];

  const entityColumns: GridColDef<EntityAuditLogDto>[] = [
    {
      field: '_expand',
      headerName: '',
      width: 40,
      sortable: false,
      renderCell: ({ row }) => (
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setEExpanded(eExpanded === row.id ? null : row.id); }}
        >
          {eExpanded === row.id ? <ChevronDown size={13} /> : <ChevronRight size={13} />}
        </IconButton>
      ),
    },
    {
      field: 'dateTime',
      headerName: 'Time',
      width: 140,
      renderCell: ({ row }) => (
        <Typography variant="body1" sx={{ fontSize: '0.7rem', fontFamily: 'monospace', color: 'text.secondary' }}>
          {dayjs(row.dateTime).format('DD MMM YY HH:mm:ss')}
        </Typography>
      ),
    },
    {
      field: 'tableName',
      headerName: 'Table',
      width: 130,
      renderCell: ({ row }) => (
        <Chip
          label={row.tableName ?? '—'}
          size="small"
          sx={{ height: 18, fontSize: '0.6rem', backgroundColor: 'rgba(138,112,214,0.1)', color: '#8A70D6', borderRadius: '4px', '& .MuiChip-label': { px: 1 } }}
        />
      ),
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 90,
      renderCell: ({ row }) => {
        const colors: Record<string, string> = { Create: '#10B981', Update: '#F59E0B', Delete: '#EF4444' };
        const color = colors[row.type ?? ''] ?? '#64748B';
        return (
          <Chip label={row.type ?? '—'} size="small" sx={{ height: 18, fontSize: '0.6rem', backgroundColor: `${color}18`, color, borderRadius: '4px', '& .MuiChip-label': { px: 1 } }} />
        );
      },
    },
    {
      field: 'userId',
      headerName: 'User ID',
      flex: 1,
      minWidth: 120,
      renderCell: ({ row }) => (
        <Typography variant="body1" sx={{ fontSize: '0.7rem', fontFamily: 'monospace', color: 'text.secondary' }} noWrap>
          {row.userId ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'primaryKey',
      headerName: 'Record ID',
      width: 90,
      renderCell: ({ row }) => (
        <Typography variant="body1" sx={{ fontSize: '0.7rem', fontFamily: 'monospace' }}>
          {row.primaryKey ?? '—'}
        </Typography>
      ),
    },
    {
      field: 'affectedColumns',
      headerName: 'Changed Fields',
      flex: 1,
      minWidth: 130,
      renderCell: ({ row }) => (
        <Typography variant="body1" sx={{ fontSize: '0.7rem', color: 'text.secondary' }} noWrap>
          {row.affectedColumns ?? '—'}
        </Typography>
      ),
    },
  ];

  const FilterBar = ({ children }: { children: React.ReactNode }) => (
    <Paper
      sx={{
        p: 1.25,
        mb: 1.5,
        display: 'flex',
        gap: 1,
        alignItems: 'center',
        border: '1px solid rgba(255,255,255,0.06)',
        flexWrap: 'wrap',
      }}
    >
      {children}
    </Paper>
  );

  const inputSx = { minWidth: 120 };
  const textFieldSx = { width: 150 };

  return (
    <Box>
      <Paper sx={{ border: '1px solid rgba(255,255,255,0.06)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2, pt: 0.5 }}>
          <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ borderBottom: 'none' }}>
            <Tab label="Tenant Audit Logs" />
            <Tab label="Entity Changes" />
          </Tabs>
          <Button
            size="small"
            startIcon={<Download size={13} />}
            onClick={handleExport}
            variant="outlined"
            sx={{ fontSize: '0.75rem', py: 0.5 }}
          >
            Export CSV
          </Button>
        </Box>

        <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.07)', p: 2 }}>
          {/* Tenant Audit Logs */}
          {tab === 0 && (
            <>
              <FilterBar>
                <TextField
                  placeholder="Institute ID"
                  value={tInstituteId}
                  onChange={(e) => { setTInstituteId(e.target.value); setTPage(0); }}
                  size="small"
                  sx={{ width: 110 }}
                />
                <FormControl size="small" sx={inputSx}>
                  <InputLabel>Event Type</InputLabel>
                  <Select label="Event Type" value={tEventType} onChange={(e) => { setTEventType(e.target.value); setTPage(0); }}>
                    <MenuItem value="">All</MenuItem>
                    {EVENT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField
                  placeholder="User ID"
                  value={tUserId}
                  onChange={(e) => { setTUserId(e.target.value); setTPage(0); }}
                  size="small"
                  sx={{ width: 120 }}
                />
                <TextField
                  type="date"
                  label="From"
                  value={tDateFrom}
                  onChange={(e) => setTDateFrom(e.target.value)}
                  size="small"
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={textFieldSx}
                />
                <TextField
                  type="date"
                  label="To"
                  value={tDateTo}
                  onChange={(e) => setTDateTo(e.target.value)}
                  size="small"
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={textFieldSx}
                />
                <Box sx={{ flex: 1 }} />
                {tenantData && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {tenantData.totalCount} events
                  </Typography>
                )}
              </FilterBar>

              {loadingTenant ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {[...Array(8)].map((_, i) => <Skeleton key={i} variant="rounded" height={44} sx={{ borderRadius: '6px' }} />)}
                </Box>
              ) : (
                <>
                  <DataGrid
                    rows={tenantData?.data ?? []}
                    columns={tenantColumns}
                    rowCount={tenantData?.totalCount ?? 0}
                    paginationMode="server"
                    paginationModel={{ page: tPage, pageSize: tPageSize }}
                    onPaginationModelChange={(m) => { setTPage(m.page); setTPageSize(m.pageSize); }}
                    pageSizeOptions={[25, 50, 100]}
                    rowHeight={48}
                    getRowId={(r) => r.id}
                    disableRowSelectionOnClick
                    sx={{ border: 'none', minHeight: 300 }}
                  />
                  {tExpanded !== null && (tenantData?.data ?? []).find((r) => r.id === tExpanded) && (
                    <ExpandableRow expanded>
                      <JsonDiff
                        oldValues={(tenantData?.data ?? []).find((r) => r.id === tExpanded)!.oldValues}
                        newValues={(tenantData?.data ?? []).find((r) => r.id === tExpanded)!.newValues}
                      />
                    </ExpandableRow>
                  )}
                </>
              )}
            </>
          )}

          {/* Entity Changes */}
          {tab === 1 && (
            <>
              <FilterBar>
                <FormControl size="small" sx={inputSx}>
                  <InputLabel>Table</InputLabel>
                  <Select label="Table" value={eTableName} onChange={(e) => { setETableName(e.target.value); setEPage(0); }}>
                    <MenuItem value="">All</MenuItem>
                    {TABLE_NAMES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" sx={{ minWidth: 100 }}>
                  <InputLabel>Type</InputLabel>
                  <Select label="Type" value={eAuditType} onChange={(e) => { setEAuditType(e.target.value); setEPage(0); }}>
                    <MenuItem value="">All</MenuItem>
                    {AUDIT_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
                <TextField
                  placeholder="User ID"
                  value={eUserId}
                  onChange={(e) => { setEUserId(e.target.value); setEPage(0); }}
                  size="small"
                  sx={{ width: 120 }}
                />
                <TextField
                  type="date"
                  label="From"
                  value={eDateFrom}
                  onChange={(e) => setEDateFrom(e.target.value)}
                  size="small"
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={textFieldSx}
                />
                <TextField
                  type="date"
                  label="To"
                  value={eDateTo}
                  onChange={(e) => setEDateTo(e.target.value)}
                  size="small"
                  slotProps={{ inputLabel: { shrink: true } }}
                  sx={textFieldSx}
                />
                <Box sx={{ flex: 1 }} />
                {entityData && (
                  <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                    {entityData.totalCount} changes
                  </Typography>
                )}
              </FilterBar>

              {loadingEntity ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {[...Array(8)].map((_, i) => <Skeleton key={i} variant="rounded" height={44} sx={{ borderRadius: '6px' }} />)}
                </Box>
              ) : (
                <>
                  <DataGrid
                    rows={entityData?.data ?? []}
                    columns={entityColumns}
                    rowCount={entityData?.totalCount ?? 0}
                    paginationMode="server"
                    paginationModel={{ page: ePage, pageSize: ePageSize }}
                    onPaginationModelChange={(m) => { setEPage(m.page); setEPageSize(m.pageSize); }}
                    pageSizeOptions={[25, 50, 100]}
                    rowHeight={48}
                    getRowId={(r) => r.id}
                    disableRowSelectionOnClick
                    sx={{ border: 'none', minHeight: 300 }}
                  />
                  {eExpanded !== null && (entityData?.data ?? []).find((r) => r.id === eExpanded) && (
                    <ExpandableRow expanded>
                      <JsonDiff
                        oldValues={(entityData?.data ?? []).find((r) => r.id === eExpanded)!.oldValues}
                        newValues={(entityData?.data ?? []).find((r) => r.id === eExpanded)!.newValues}
                      />
                    </ExpandableRow>
                  )}
                </>
              )}
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
}
